import { Injectable, computed, inject, signal } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import {
  Observable,
  catchError,
  forkJoin,
  map,
  of,
  switchMap,
  tap,
} from 'rxjs';

import { environment } from '../../environments/environment';

// Convenience flag: true when running in mock mode (no backend URL configured or tests/dev mocks enabled)
// When an API base URL is not configured, the service falls back to local-only mode.
// Prefer calling the backend when environment.apiUrl is set.
import {
  MOCK_ASSET_REGISTER,
  MOCK_GOODS_RECEIVED,
  MOCK_INVENTORY_ITEMS,
  MOCK_STOCK_ADJUSTMENTS,
  MOCK_STOCK_ISSUES,
  MOCK_STOCK_SNAPSHOTS,
  MOCK_STOCK_TRANSFERS,
} from '../data/mock-inventory.data';
import {
  GenericMultiResultDto,
  StockTransfersResponseDto,
} from '../models/api.models';
import { ListQuery, PaginatedResult } from '../models/common.models';
import {
  AssetRegisterEntry,
  GoodsReceivedNote,
  InventoryItem,
  InventoryItemStats,
  ItemHistoryAsset,
  ItemHistoryEvent,
  ItemHistoryHeader,
  ItemHistoryLocation,
  ItemHistoryView,
  StockAdjustment,
  StockIssue,
  StockLocationSuggestion,
  StockSnapshot,
  StockTransfer,
} from '../models/inventory.models';
import { ApiService } from './api.service';
import { AuthService } from '../../modules/auth/auth.service';
import {
  findResultSetWithColumn,
  asDict,
  allResultSets,
  pick,
  pickDate,
  pickNumber,
  pickString,
  resultSetAt,
} from '../utils/api-shape.util';
import {
  resolveBranchId,
  filterRowsByUserBranch,
  branchQueryParams,
} from '../utils/branch-scope.util';
import { remainingAvailable } from '../utils/stock-qty.util';

const STORAGE_KEY = 'ims-inventory-data';

export interface InventoryApiResult {
  success: boolean;
  message?: string;
  inventoryItemId?: number;
}

@Injectable({ providedIn: 'root' })
export class InventoryService {
  private readonly api = inject(ApiService);
  private readonly auth = inject(AuthService);

  private readonly items = signal<InventoryItem[]>([]);
  private readonly stockSnapshots = signal<StockSnapshot[]>([]);
  private readonly assets = signal<AssetRegisterEntry[]>([]);
  private readonly goodsReceived = signal<GoodsReceivedNote[]>([]);
  private readonly stockIssues = signal<StockIssue[]>([]);
  private readonly stockAdjustments = signal<StockAdjustment[]>([]);
  private readonly stockTransfers = signal<StockTransfer[]>([]);

  readonly allItems = this.items.asReadonly();
  readonly allStockSnapshots = this.stockSnapshots.asReadonly();

  /** Active + rentable items from Register Items — shared gate for Item Rental lists. */
  readonly rentableInventoryItems = computed(() =>
    this.allItems().filter((item) => this.isEligibleForEquipmentRental(item)),
  );
  readonly allAssets = this.assets.asReadonly();
  readonly allGoodsReceived = this.goodsReceived.asReadonly();
  readonly allStockIssues = this.stockIssues.asReadonly();
  /** Completed issued-out rows only (excludes pending, cancelled, and reserves). */
  readonly issuedOutStock = computed(() =>
    this.stockIssues().filter((row) => row.status === 'issued'),
  );
  /** Reserved holds waiting to be issued (or cancelled). */
  readonly reservedIssueHolds = computed(() =>
    this.stockIssues().filter(
      (row) => row.status === 'reserved' || row.isReservedHold === true,
    ),
  );
  /** Issued + reserved holds for Issue Stock / Issued Stock lists. */
  readonly issueStockList = computed(() =>
    this.stockIssues().filter((row) => row.status !== 'cancelled'),
  );
  readonly allStockAdjustments = this.stockAdjustments.asReadonly();
  readonly allStockTransfers = this.stockTransfers.asReadonly();

  readonly itemStats = computed<InventoryItemStats>(() => {
    const list = this.items();
    return {
      totalItems: list.length,
      activeItems: list.filter((item) => item.status === 'active').length,
      lowStock: list.filter((item) => item.stockLevel <= item.reorderLevel)
        .length,
      discontinued: list.filter((item) => item.status === 'discontinued')
        .length,
    };
  });

  readonly lowStockItems = computed(() =>
    this.items().filter((item) => item.stockLevel <= item.reorderLevel),
  );

  readonly totalInventoryValue = computed(() =>
    this.items().reduce((sum, item) => sum + item.stockLevel * item.price, 0),
  );

  readonly pendingAdjustments = computed(
    () =>
      this.stockAdjustments().filter((row) => row.status === 'pending').length,
  );

  constructor() {
    if (!environment.apiUrl) {
      this.syncStockSnapshots();
    } else if (sessionStorage.getItem('ims-auth')) {
      this.refreshFromApi();
    }
  }

  refreshFromApi(): void {
    if (!environment.apiUrl) {
      return;
    }

    const branchParams = branchQueryParams(this.auth);

    this.loadItemMasterAndStock().subscribe();

    this.api
      .get<GenericMultiResultDto>('/GoodsReceived', branchParams)
      .pipe(
        tap((payload) => this.applyGoodsReceived(payload)),
        catchError(() => of(null)),
      )
      .subscribe();

    this.api
      .get<GenericMultiResultDto>('/IssueStock', branchParams)
      .pipe(
        tap((payload) => this.applyIssueStock(payload)),
        catchError(() => of(null)),
      )
      .subscribe();

    this.api
      .get<StockTransfersResponseDto>('/StockTransfers', branchParams)
      .pipe(
        tap((payload) => this.applyStockTransfers(payload)),
        catchError(() => of(null)),
      )
      .subscribe();
  }

  /** Same rules as Register Items "Rentable" filter + active status. */
  isEligibleForEquipmentRental(item: {
    status: InventoryItem['status'] | string;
    isRentable?: boolean;
  }): boolean {
    return item.status === 'active' && item.isRentable === true;
  }

  findItemById(inventoryItemId: string | number): InventoryItem | undefined {
    const id = String(inventoryItemId);
    return this.allItems().find((item) => item.id === id);
  }

  /** On-hand minus reserved at a branch (from Current Stock snapshots). */
  availableQuantityAtBranch(
    inventoryItemId: string | number,
    branchId?: string,
  ): number {
    const id = String(inventoryItemId);
    const branchNum = branchId && branchId !== 'all' ? Number(branchId) : NaN;

    return this.allStockSnapshots()
      .filter((snap) => {
        if (String(snap.inventoryItemId ?? '') !== id) {
          return false;
        }
        if (!branchId || branchId === 'all') {
          return true;
        }
        if (snap.branchId) {
          return (
            snap.branchId === branchId ||
            (Number.isFinite(branchNum) && Number(snap.branchId) === branchNum)
          );
        }
        return false;
      })
      .reduce(
        (sum, snap) =>
          sum + remainingAvailable(snap.quantity, snap.reservedQuantity ?? 0),
        0,
      );
  }

  /** On-hand minus reserved at a branch + store location. */
  availableQuantityAtLocation(
    inventoryItemId: string | number,
    branchId?: string,
    locationId?: string,
    locationName?: string,
  ): number {
    const id = String(inventoryItemId);
    const branchNum = branchId && branchId !== 'all' ? Number(branchId) : NaN;
    const locLabel = locationName?.trim().toLowerCase();

    return this.allStockSnapshots()
      .filter((snap) => {
        if (String(snap.inventoryItemId ?? '') !== id) {
          return false;
        }
        if (branchId && branchId !== 'all') {
          if (snap.branchId) {
            const branchMatches =
              snap.branchId === branchId ||
              (Number.isFinite(branchNum) &&
                Number(snap.branchId) === branchNum);
            if (!branchMatches) {
              return false;
            }
          }
        }
        if (locationId && locationId !== 'all') {
          if (snap.locationId) {
            return snap.locationId === locationId;
          }
          if (locLabel) {
            return snap.location.trim().toLowerCase() === locLabel;
          }
          return false;
        }
        return true;
      })
      .reduce(
        (sum, snap) =>
          sum + remainingAvailable(snap.quantity, snap.reservedQuantity ?? 0),
        0,
      );
  }

  hasStockAtLocation(
    inventoryItemId: string | number,
    branchId?: string,
    locationId?: string,
    locationName?: string,
  ): boolean {
    return (
      this.availableQuantityAtLocation(
        inventoryItemId,
        branchId,
        locationId,
        locationName,
      ) > 0
    );
  }

  /** Other Current Stock bins that still have available qty for this item. */
  suggestStockLocations(
    inventoryItemId: string | number,
    exclude?: { branchId?: string; locationId?: string },
  ): StockLocationSuggestion[] {
    const id = String(inventoryItemId);
    const excludeBranch = (exclude?.branchId ?? '').trim();
    const excludeLocation = (exclude?.locationId ?? '').trim();

    return this.allStockSnapshots()
      .filter((snap) => {
        if (String(snap.inventoryItemId ?? '') !== id) {
          return false;
        }
        const available = remainingAvailable(
          snap.quantity,
          snap.reservedQuantity ?? 0,
        );
        if (available <= 0) {
          return false;
        }
        const sameBranch = excludeBranch && snap.branchId === excludeBranch;
        const sameLocation =
          excludeLocation && snap.locationId === excludeLocation;
        if (sameBranch && (sameLocation || !excludeLocation)) {
          if (excludeLocation) {
            return snap.locationId !== excludeLocation;
          }
          return false;
        }
        if (sameBranch && sameLocation) {
          return false;
        }
        return true;
      })
      .map((snap) => ({
        inventoryItemId: id,
        itemName: snap.itemName,
        branchId: snap.branchId,
        branchName: snap.branch || '',
        locationId: snap.locationId,
        locationName: snap.location || '',
        available: remainingAvailable(
          snap.quantity,
          snap.reservedQuantity ?? 0,
        ),
      }))
      .sort((a, b) => b.available - a.available);
  }

  formatStockSuggestions(suggestions: StockLocationSuggestion[]): string {
    if (!suggestions.length) {
      return '';
    }
    return suggestions
      .slice(0, 5)
      .map((row) => {
        const place = [row.branchName, row.locationName]
          .filter(Boolean)
          .join(' / ');
        return `${place} (${row.available})`;
      })
      .join('; ');
  }

  /** Loads Register Items + Current Stock together, then syncs Register Items qty from all location balances. */
  loadItemMasterAndStock(): Observable<void> {
    if (!environment.apiUrl) {
      return of(undefined);
    }

    const branchParams = branchQueryParams(this.auth);
    return forkJoin([
      this.api
        .get<GenericMultiResultDto>('/ItemMaster', branchParams)
        .pipe(catchError(() => of(null))),
      this.api
        .get<GenericMultiResultDto>('/CurrentStock', branchParams)
        .pipe(catchError(() => of(null))),
    ]).pipe(
      tap(([itemMaster, stock]) => {
        if (itemMaster) {
          this.applyItemMaster(itemMaster);
        }
        if (stock) {
          this.applyCurrentStock(stock);
        }
        this.syncItemMasterStockFromSnapshots();
      }),
      map(() => undefined),
    );
  }

  fetchItemHistory(
    inventoryItemId: string | number,
    range?: { startDate?: string; endDate?: string },
  ): Observable<{
    success: boolean;
    message?: string;
    data?: ItemHistoryView;
  }> {
    const id = Number(inventoryItemId);
    if (!Number.isFinite(id) || id <= 0) {
      return of({ success: false, message: 'Select an item to track.' });
    }

    if (!environment.apiUrl) {
      return of({ success: true, data: this.buildMockItemHistory(id) });
    }

    const params: Record<string, string | number | boolean> = {};
    if (range?.startDate) {
      params['startDate'] = range.startDate;
    }
    if (range?.endDate) {
      params['endDate'] = range.endDate;
    }

    return this.api
      .get<GenericMultiResultDto>(`/ItemHistory/${id}`, params)
      .pipe(
        map((payload) => ({
          success: true as const,
          data: this.parseItemHistory(payload, id),
        })),
        catchError((error) =>
          of(this.toApiError(error, 'Failed to load item history.')),
        ),
      );
  }

  private parseItemHistory(
    payload: GenericMultiResultDto,
    fallbackId: number,
  ): ItemHistoryView {
    const sets = allResultSets(payload);
    const root = asDict(payload);
    const summary = asDict(root['summary'] ?? root['Summary']);
    const summaryHasItem =
      pickNumber(summary, 'InventoryItemID', 'inventoryItemId') > 0 ||
      !!pickString(summary, 'ItemName', 'itemName', 'ItemCode', 'itemCode');
    const headerFromSummary = this.mapItemHistoryHeader(summary, fallbackId);
    const headerFromSet = this.mapItemHistoryHeader(
      asDict(sets[0]?.[0]),
      fallbackId,
    );
    const header = summaryHasItem ? headerFromSummary : headerFromSet;

    const locations = findResultSetWithColumn(
      payload,
      'StockBalanceID',
      'QuantityOnHand',
      'LocationName',
    );
    const timeline = findResultSetWithColumn(
      payload,
      'EventDate',
      'EventType',
      'Direction',
    );
    const assets = findResultSetWithColumn(
      payload,
      'EquipmentAssetID',
      'AssetNumber',
      'AssetStatus',
    );

    // Fallback to positional sets when column sniffing finds nothing
    // (Summary holds header → ResultSets are locations, timeline, assets).
    const locRows = locations.length
      ? locations
      : summaryHasItem
        ? (sets[0] ?? [])
        : (sets[1] ?? []);
    const timelineRows = timeline.length
      ? timeline
      : summaryHasItem
        ? (sets[1] ?? [])
        : (sets[2] ?? []);
    const assetRows = assets.length
      ? assets
      : summaryHasItem
        ? (sets[2] ?? [])
        : (sets[3] ?? []);

    return {
      header: header.inventoryItemId > 0 || header.name ? header : null,
      locations: locRows.map((row) => this.mapItemHistoryLocation(row)),
      timeline: timelineRows.map((row) => this.mapItemHistoryEvent(row)),
      assets: assetRows.map((row) => this.mapItemHistoryAsset(row)),
    };
  }

  private mapItemHistoryHeader(
    row: unknown,
    fallbackId: number,
  ): ItemHistoryHeader {
    return {
      inventoryItemId:
        pickNumber(row, 'InventoryItemID', 'inventoryItemId') || fallbackId,
      code: pickString(row, 'ItemCode', 'itemCode', 'code'),
      name: pickString(row, 'ItemName', 'itemName', 'name'),
      description: pickString(
        row,
        'ItemDescription',
        'itemDescription',
        'description',
      ),
      category: pickString(row, 'CategoryName', 'category'),
      unit: pickString(row, 'UnitName', 'unit'),
      itemType: pickString(row, 'ItemType', 'itemType'),
      trackingMethod: pickString(row, 'TrackingMethod', 'trackingMethod'),
      isRentable:
        pickString(row, 'IsRentable', 'isRentable').toLowerCase() === 'true' ||
        pickNumber(row, 'IsRentable') === 1,
      isConsumable:
        pickString(row, 'IsConsumable', 'isConsumable').toLowerCase() ===
          'true' || pickNumber(row, 'IsConsumable') === 1,
      isActive:
        pickString(row, 'IsActive', 'isActive').toLowerCase() !== 'false' &&
        pickNumber(row, 'IsActive') !== 0,
      reorderLevel: pickNumber(row, 'ReorderLevel', 'reorderLevel'),
      standardCost: pickNumber(row, 'StandardCost', 'standardCost'),
      sellingPrice: pickNumber(row, 'SellingPrice', 'sellingPrice'),
      totalOnHand: pickNumber(row, 'TotalOnHand', 'totalOnHand'),
      totalReserved: pickNumber(row, 'TotalReserved', 'totalReserved'),
      totalAvailable: pickNumber(row, 'TotalAvailable', 'totalAvailable'),
      stockValue: pickNumber(row, 'StockValue', 'stockValue'),
      locationCount: pickNumber(row, 'LocationCount', 'locationCount'),
      lastMovementDate: pickDate(row, 'LastMovementDate', 'lastMovementDate'),
    };
  }

  private mapItemHistoryLocation(row: unknown): ItemHistoryLocation {
    const onHand = pickNumber(
      row,
      'QuantityOnHand',
      'quantityOnHand',
      'OnHand',
    );
    const reserved = pickNumber(
      row,
      'QuantityReserved',
      'quantityReserved',
      'Reserved',
    );
    return {
      stockBalanceId: pickString(row, 'StockBalanceID', 'stockBalanceId', 'id'),
      branchId: pickString(row, 'BranchID', 'branchId'),
      branchName: pickString(row, 'BranchName', 'branchName', 'branch'),
      locationId: pickString(
        row,
        'StoreLocationID',
        'storeLocationID',
        'locationId',
      ),
      locationName: pickString(row, 'LocationName', 'locationName', 'location'),
      locationType: pickString(row, 'LocationType', 'locationType'),
      onHand,
      reserved,
      available:
        pickNumber(row, 'QuantityAvailable', 'quantityAvailable') ||
        remainingAvailable(onHand, reserved),
      status: pickString(row, 'Status', 'status'),
      lastUpdated: pickDate(
        row,
        'LastUpdatedDate',
        'lastUpdatedDate',
        'LastUpdated',
      ),
    };
  }

  private mapItemHistoryEvent(row: unknown): ItemHistoryEvent {
    const directionRaw = pickString(
      row,
      'Direction',
      'direction',
    ).toLowerCase();
    const direction: ItemHistoryEvent['direction'] =
      directionRaw === 'in' ||
      directionRaw === 'out' ||
      directionRaw === 'move' ||
      directionRaw === 'hold'
        ? directionRaw
        : 'out';
    return {
      eventDate:
        pickString(row, 'EventDate', 'eventDate') ||
        pickDate(row, 'EventDate', 'eventDate'),
      eventType: pickString(row, 'EventType', 'eventType'),
      direction,
      quantity: pickNumber(row, 'Quantity', 'quantity'),
      unitCost: pickNumber(row, 'UnitCost', 'unitCost'),
      reference: pickString(row, 'Reference', 'reference'),
      branchName: pickString(row, 'BranchName', 'branchName'),
      fromLocation: pickString(row, 'FromLocation', 'fromLocation'),
      toLocation: pickString(row, 'ToLocation', 'toLocation'),
      party: pickString(row, 'Party', 'party'),
      contactPhone: pickString(row, 'ContactPhone', 'contactPhone'),
      destination: pickString(row, 'Destination', 'destination'),
      userName: pickString(row, 'UserName', 'userName'),
      status: pickString(row, 'Status', 'status'),
      notes: pickString(row, 'Notes', 'notes'),
    };
  }

  private mapItemHistoryAsset(row: unknown): ItemHistoryAsset {
    return {
      id: pickString(row, 'EquipmentAssetID', 'equipmentAssetID', 'id'),
      assetNumber: pickString(row, 'AssetNumber', 'assetNumber'),
      serialNumber: pickString(row, 'SerialNumber', 'serialNumber'),
      name: pickString(row, 'EquipmentName', 'equipmentName', 'name'),
      status: pickString(row, 'AssetStatus', 'assetStatus', 'status'),
      condition: pickString(
        row,
        'CurrentCondition',
        'currentCondition',
        'condition',
      ),
      branchName: pickString(row, 'BranchName', 'branchName'),
      locationName: pickString(row, 'LocationName', 'locationName'),
      purchaseDate: pickDate(row, 'PurchaseDate', 'purchaseDate'),
      purchaseCost: pickNumber(row, 'PurchaseCost', 'purchaseCost'),
    };
  }

  private buildMockItemHistory(inventoryItemId: number): ItemHistoryView {
    const item = this.findItemById(inventoryItemId);
    const locations = this.allStockSnapshots()
      .filter(
        (snap) => String(snap.inventoryItemId) === String(inventoryItemId),
      )
      .map((snap) => ({
        stockBalanceId: snap.id,
        branchId: snap.branchId ?? '',
        branchName: snap.branch ?? '',
        locationId: snap.locationId ?? '',
        locationName: snap.location,
        locationType: '',
        onHand: snap.quantity,
        reserved: snap.reservedQuantity ?? 0,
        available: remainingAvailable(
          snap.quantity,
          snap.reservedQuantity ?? 0,
        ),
        status: snap.stockStatus,
        lastUpdated: snap.lastUpdated ?? '',
      }));
    const timeline: ItemHistoryEvent[] = [
      ...this.goodsReceived()
        .filter(
          (row) =>
            (row.itemName || '').toLowerCase() ===
            (item?.name || '').toLowerCase(),
        )
        .map((row) => ({
          eventDate: row.receivedDate,
          eventType: 'Received',
          direction: 'in' as const,
          quantity: row.items,
          unitCost: row.items ? row.totalValue / row.items : 0,
          reference: row.grnNumber,
          branchName: row.branch ?? '',
          fromLocation: '',
          toLocation: row.location ?? '',
          party: row.supplier,
          contactPhone: '',
          destination: '',
          userName: '',
          status: row.status,
          notes: '',
        })),
      ...this.stockIssues()
        .filter(
          (row) =>
            String(row.inventoryItemId || '') === String(inventoryItemId) ||
            (row.itemName || '')
              .toLowerCase()
              .includes((item?.name || '').toLowerCase()),
        )
        .map((row) => ({
          eventDate: row.issueDate,
          eventType: 'Issued',
          direction: 'out' as const,
          quantity: row.items,
          unitCost: 0,
          reference: row.issueNumber,
          branchName: row.branch,
          fromLocation: row.location ?? '',
          toLocation: '',
          party: row.issuedTo,
          contactPhone: row.recipientPhone ?? '',
          destination: row.recipientLocation ?? '',
          userName: '',
          status: row.status,
          notes: '',
        })),
    ].sort((a, b) => b.eventDate.localeCompare(a.eventDate));

    const onHand = locations.reduce((sum, row) => sum + row.onHand, 0);
    const reserved = locations.reduce((sum, row) => sum + row.reserved, 0);
    return {
      header: item
        ? {
            inventoryItemId,
            code: item.code,
            name: item.name,
            description: '',
            category: item.category,
            unit: item.unit,
            itemType: item.isRentable ? 'Rentable' : 'Consumable',
            trackingMethod: '',
            isRentable: !!item.isRentable,
            isConsumable: !item.isRentable,
            isActive: item.status === 'active',
            reorderLevel: item.reorderLevel,
            standardCost: item.price,
            sellingPrice: item.price,
            totalOnHand: onHand,
            totalReserved: reserved,
            totalAvailable: remainingAvailable(onHand, reserved),
            stockValue: onHand * item.price,
            locationCount: locations.length,
            lastMovementDate: timeline[0]?.eventDate ?? '',
          }
        : null,
      locations,
      timeline,
      assets: [],
    };
  }

  /** Full inventory refresh after stock movements (transfer, issue, receive, adjust). */
  refreshInventoryAfterStockMove(): Observable<void> {
    if (!environment.apiUrl) {
      return of(undefined);
    }

    const branchParams = branchQueryParams(this.auth);
    return this.loadItemMasterAndStock().pipe(
      switchMap(() =>
        this.api
          .get<StockTransfersResponseDto>('/StockTransfers', branchParams)
          .pipe(
            tap((payload) => this.applyStockTransfers(payload)),
            catchError(() => of(null)),
            map(() => undefined),
          ),
      ),
    );
  }

  /**
   * Register Items shows one row per item; Current Stock has one row per location.
   * Keep Register Items stockLevel as the sum of on-hand across all loaded location balances.
   */
  private syncItemMasterStockFromSnapshots(): void {
    const totals = new Map<
      string,
      {
        onHand: number;
        reserved: number;
        location?: string;
        locationId?: string;
        branch?: string;
        branchId?: string;
        topLocationQty: number;
      }
    >();

    for (const snap of this.stockSnapshots()) {
      const itemId = (snap as StockSnapshot & { inventoryItemId?: string })
        .inventoryItemId;
      if (!itemId) {
        continue;
      }
      const qty = snap.quantity || 0;
      const prev = totals.get(itemId) ?? {
        onHand: 0,
        reserved: 0,
        topLocationQty: -1,
      };
      const useLocation = qty >= prev.topLocationQty;
      totals.set(itemId, {
        onHand: prev.onHand + qty,
        reserved: prev.reserved + (snap.reservedQuantity || 0),
        location: useLocation ? snap.location || prev.location : prev.location,
        locationId: useLocation
          ? snap.locationId || prev.locationId
          : prev.locationId,
        branch: snap.branch || prev.branch,
        branchId: snap.branchId || prev.branchId,
        topLocationQty: useLocation ? qty : prev.topLocationQty,
      });
    }

    if (!totals.size) {
      return;
    }

    this.items.update((list) =>
      list.map((item) => {
        const total = totals.get(item.id);
        if (!total) {
          return item;
        }
        return {
          ...item,
          stockLevel: total.onHand,
          branch: total.branch || item.branch,
          branchId: total.branchId || item.branchId,
          location: total.location || item.location,
          locationId: total.locationId || item.locationId,
        };
      }),
    );
  }

  /** Loads asset register rows from GET /EquipmentCheckout (equipment assets grid). */
  refreshAssetsFromApi(): void {
    if (!environment.apiUrl) {
      return;
    }

    this.api
      .get<GenericMultiResultDto>('/EquipmentCheckout')
      .pipe(
        tap((payload) => this.applyAssetsFromCheckout(payload)),
        catchError(() => of(null)),
      )
      .subscribe();
  }

  private toApiError(error: unknown, fallback: string): InventoryApiResult {
    if (error instanceof HttpErrorResponse) {
      const body = error.error;
      if (typeof body === 'string' && body.trim()) {
        return { success: false, message: body };
      }
      if (typeof body === 'object' && body !== null) {
        const dict = body as Record<string, unknown>;
        const validationMessage = this.formatValidationErrors(
          dict['errors'] ?? dict['Errors'],
        );
        if (validationMessage) {
          return { success: false, message: validationMessage };
        }
        const detailed =
          dict['error'] ??
          dict['Error'] ??
          dict['message'] ??
          dict['Message'] ??
          dict['title'];
        if (detailed != null && String(detailed).trim()) {
          const base = String(detailed);
          // Prefer SQL/detail text when title is the generic ASP.NET validation banner.
          if (
            base.toLowerCase().includes('validation errors') &&
            dict['detail']
          ) {
            return { success: false, message: String(dict['detail']) };
          }
          return { success: false, message: base };
        }
      }
      if (error.status === 404) {
        return {
          success: false,
          message:
            'Stock adjust API was not found. Restart IMS.Gateway after rebuilding so POST /CurrentStock/adjust is available.',
        };
      }
      return { success: false, message: error.message || fallback };
    }

    return { success: false, message: fallback };
  }

  /** Flattens ASP.NET ValidationProblemDetails.errors into one toast-friendly string. */
  private formatValidationErrors(errors: unknown): string {
    if (!errors || typeof errors !== 'object') {
      return '';
    }
    const parts: string[] = [];
    for (const [field, value] of Object.entries(
      errors as Record<string, unknown>,
    )) {
      const messages = Array.isArray(value)
        ? value.map((v) => String(v))
        : [String(value)];
      for (const msg of messages) {
        if (msg.trim()) {
          parts.push(field && field !== '' ? `${field}: ${msg}` : msg);
        }
      }
    }
    return parts.join(' ');
  }

  private postInventory<T>(
    path: string,
    body: unknown,
    fallbackError: string,
  ): Observable<InventoryApiResult> {
    return this.api.post<T>(path, body).pipe(
      tap(() => this.refreshFromApi()),
      map(() => ({ success: true })),
      catchError((error) => of(this.toApiError(error, fallbackError))),
    );
  }

  /** POST /ItemMaster/add — creates catalog item on the gateway. */
  createItemViaApi(
    item: Omit<InventoryItem, 'id'> & {
      categoryId?: string;
      unitId?: string;
      branchId?: string;
      locationId?: string;
    },
  ): Observable<InventoryApiResult> {
    if (!environment.apiUrl) {
      const created = this.addItem(item);
      return of({
        success: true,
        inventoryItemId: Number(created.id) || undefined,
      });
    }

    const categoryId = Number(item.categoryId ?? item.category);
    const unitId = Number(item.unitId ?? item.unit);
    const branchId = resolveBranchId(this.auth, item.branchId ?? item.branch);
    const locationId = Number(item.locationId ?? item.location);

    return this.api
      .post<unknown>('/ItemMaster/add', {
        itemName: item.name,
        itemDescription: item.name,
        itemCategoryID:
          Number.isFinite(categoryId) && categoryId > 0 ? categoryId : 0,
        unitOfMeasureID: Number.isFinite(unitId) && unitId > 0 ? unitId : 0,
        branchID: branchId,
        storeLocationID:
          Number.isFinite(locationId) && locationId > 0
            ? locationId
            : undefined,
        quantityOnHand: item.stockLevel,
        reorderLevel: item.reorderLevel,
        itemType: item.isRentable ? 'RENTAL_EQUIPMENT' : 'CONSUMABLE',
        trackingMethod: 'Quantity',
        standardCost: item.price,
        sellingPrice: item.price,
        isConsumable: !item.isRentable,
        isRentable: !!item.isRentable,
        isReturnable: !!item.isRentable,
        isActive: item.status === 'active',
      })
      .pipe(
        switchMap((payload) => {
          const inventoryItemId = this.extractInventoryItemId(payload);
          return this.refreshInventoryAfterStockMove().pipe(
            map(() => ({
              success: true as const,
              inventoryItemId:
                inventoryItemId > 0 ? inventoryItemId : undefined,
            })),
          );
        }),
        catchError((error) =>
          of(this.toApiError(error, 'Failed to add inventory item.')),
        ),
      );
  }

  private extractInventoryItemId(payload: unknown): number {
    if (!payload || typeof payload !== 'object') {
      return 0;
    }

    const root = payload as Record<string, unknown>;
    const direct = pickNumber(
      root,
      'inventoryItemId',
      'InventoryItemID',
      'itemId',
      'ItemId',
    );
    if (direct > 0) {
      return direct;
    }

    const data = root['data'];
    if (data && typeof data === 'object') {
      const dataObj = data as Record<string, unknown>;
      const fromData = pickNumber(
        dataObj,
        'inventoryItemId',
        'InventoryItemID',
      );
      if (fromData > 0) {
        return fromData;
      }

      const summary = dataObj['summary'] ?? dataObj['Summary'];
      const fromSummary = pickNumber(
        summary,
        'InventoryItemID',
        'inventoryItemID',
        'inventoryItemId',
      );
      if (fromSummary > 0) {
        return fromSummary;
      }

      const sets = (dataObj['resultSets'] ?? dataObj['ResultSets']) as
        | unknown[]
        | undefined;
      if (Array.isArray(sets)) {
        for (const set of sets) {
          if (!Array.isArray(set) || !set.length) {
            continue;
          }
          const fromRow = pickNumber(
            set[0],
            'InventoryItemID',
            'inventoryItemID',
            'inventoryItemId',
          );
          if (fromRow > 0) {
            return fromRow;
          }
        }
      }
    }

    return 0;
  }

  /** POST /GoodsReceived */
  createGoodsReceivedViaApi(
    note: Omit<GoodsReceivedNote, 'id' | 'grnNumber'> & {
      supplierId?: string;
      branchId?: string;
      locationId?: string;
      lines?: Array<{
        inventoryItemID: number;
        quantity: number;
        unitCost?: number;
      }>;
    },
  ): Observable<InventoryApiResult> {
    if (!environment.apiUrl) {
      this.addGoodsReceived(note);
      return of({ success: true });
    }

    const lines = (note.lines ?? []).filter(
      (line) => line.inventoryItemID > 0 && line.quantity > 0,
    );
    if (!lines.length) {
      return of({
        success: false,
        message: 'Add at least one line item with a quantity.',
      });
    }

    const statusMap: Record<string, string> = {
      draft: 'Pending',
      posted: 'Completed',
      cancelled: 'Cancelled',
      Pending: 'Pending',
      Completed: 'Completed',
      Cancelled: 'Cancelled',
      Returned: 'Cancelled',
      Draft: 'Pending',
    };

    return this.postInventory(
      '/GoodsReceived',
      {
        supplierID: Number(note.supplierId ?? note.supplier) || 0,
        receivedDate: note.receivedDate,
        branchID: Number(note.branchId ?? note.branch) || 0,
        storeLocationID: Number(note.locationId ?? note.location) || 0,
        createdByUserID: this.auth.currentUserId(),
        itemsAreRentable: !!note.isRentable,
        referenceNumber: null,
        notes: null,
        status: statusMap[note.status] ?? 'Completed',
        items: lines.map((line) => ({
          InventoryItemID: line.inventoryItemID,
          Quantity: line.quantity,
          UnitCost: line.unitCost ?? 0,
          Notes: null,
        })),
      },
      'Failed to create goods receipt.',
    );
  }

  /** POST /StockTransfers — multi-line transfer (Pending by default). */
  createStockTransferViaApi(input: {
    fromBranchID: number;
    toBranchID: number;
    fromLocationID: number;
    toLocationID: number;
    transferDate: string;
    status?: StockTransfer['status'];
    notes?: string | null;
    referenceNumber?: string | null;
    fromBranch?: string;
    toBranch?: string;
    fromLocation?: string;
    toLocation?: string;
    lines: Array<{
      inventoryItemID: number;
      quantity: number;
      itemName?: string;
    }>;
  }): Observable<InventoryApiResult> {
    const lines = (input.lines ?? []).filter(
      (line) => line.inventoryItemID > 0 && line.quantity > 0,
    );
    if (!lines.length) {
      return of({ success: false, message: 'Add at least one transfer line.' });
    }

    const totalQty = lines.reduce((sum, line) => sum + line.quantity, 0);
    const itemName = lines
      .map((line) => line.itemName)
      .filter((name): name is string => !!name?.trim())
      .join(', ');
    const status = input.status ?? 'pending';
    const fromLabel = [input.fromBranch, input.fromLocation]
      .filter(Boolean)
      .join(' / ');
    const toLabel = [input.toBranch, input.toLocation]
      .filter(Boolean)
      .join(' / ');

    if (!environment.apiUrl) {
      this.addStockTransfer({
        fromLocation: fromLabel,
        toLocation: toLabel,
        fromBranch: input.fromBranch,
        toBranch: input.toBranch,
        itemName,
        transferDate: input.transferDate,
        items: totalQty,
        status,
      });
      return of({ success: true });
    }

    const createdByUserID = this.auth.currentUserId();
    if (!createdByUserID || createdByUserID <= 0) {
      return of({
        success: false,
        message:
          'Your user session has no database user id. Sign out, sign in again, then retry.',
      });
    }

    const statusMap: Record<StockTransfer['status'], string> = {
      pending: 'Pending',
      'in-transit': 'In Transit',
      completed: 'Completed',
      cancelled: 'Cancelled',
    };

    return this.api
      .post<unknown>('/StockTransfers', {
        fromBranchID: input.fromBranchID,
        toBranchID: input.toBranchID,
        fromLocationID: input.fromLocationID,
        toLocationID: input.toLocationID,
        transferDate: input.transferDate || null,
        status: statusMap[status] ?? 'Pending',
        referenceNumber: input.referenceNumber?.trim() || '',
        notes: input.notes?.trim() || '',
        createdByUserID,
        lines: lines.map((line) => ({
          inventoryItemID: line.inventoryItemID,
          quantity: line.quantity,
        })),
      })
      .pipe(
        switchMap((payload) => {
          const parsed = this.parseStockTransferProcResult(
            payload,
            'Failed to create stock transfer.',
          );
          if (!parsed.success) {
            return of(parsed);
          }

          const id =
            parsed.id && Number(parsed.id) > 0 ? String(Number(parsed.id)) : '';
          const transferNumber =
            parsed.transferNumber || (id ? `TRF-${id}` : '');

          if (id) {
            this.stockTransfers.update((list) => [
              {
                id,
                transferNumber,
                fromLocation: fromLabel,
                toLocation: toLabel,
                fromBranch: input.fromBranch,
                toBranch: input.toBranch,
                fromLocationName: input.fromLocation,
                toLocationName: input.toLocation,
                itemName,
                transferDate: input.transferDate,
                items: totalQty,
                status,
              },
              ...list.filter((row) => row.id !== id),
            ]);
          }

          // Always reload Register Items + Current Stock + transfers after a stock move.
          return this.refreshInventoryAfterStockMove().pipe(
            map(() => ({ success: true as const, message: parsed.message })),
          );
        }),
        catchError((error) =>
          of(this.toApiError(error, 'Failed to create stock transfer.')),
        ),
      );
  }

  /** PUT /StockTransfers/{id} — update status (Complete moves stock). */
  updateStockTransferViaApi(
    id: string,
    patch: {
      status: StockTransfer['status'];
      transferDate?: string;
      notes?: string | null;
      referenceNumber?: string | null;
    },
  ): Observable<InventoryApiResult> {
    if (!environment.apiUrl) {
      this.updateStockTransfer(id, patch);
      return of({ success: true });
    }

    const stockTransactionId = Number(id);
    if (!Number.isFinite(stockTransactionId) || stockTransactionId <= 0) {
      return of({
        success: false,
        message: 'Invalid transfer id. Refresh Stock Transfers and try again.',
      });
    }

    const statusMap: Record<StockTransfer['status'], string> = {
      pending: 'Pending',
      'in-transit': 'In Transit',
      completed: 'Completed',
      cancelled: 'Cancelled',
    };

    return this.api
      .put<unknown>(
        `/StockTransfers/${stockTransactionId}`,
        (() => {
          const body: Record<string, unknown> = {
            status: statusMap[patch.status] ?? 'Pending',
          };
          // Only send optional fields when the caller set them — empty string would wipe Notes/Reference.
          if (patch.transferDate !== undefined) {
            body['transferDate'] = patch.transferDate || null;
          }
          if (patch.notes !== undefined) {
            body['notes'] = patch.notes?.trim() || '';
          }
          if (patch.referenceNumber !== undefined) {
            body['referenceNumber'] = patch.referenceNumber?.trim() || '';
          }
          return body;
        })(),
      )
      .pipe(
        switchMap((payload) => {
          const parsed = this.parseStockTransferProcResult(
            payload,
            'Failed to update stock transfer.',
          );
          if (!parsed.success) {
            return of(parsed);
          }

          this.updateStockTransfer(id, {
            status: patch.status,
            transferDate: patch.transferDate,
          });
          return this.refreshInventoryAfterStockMove().pipe(
            map(() => ({ success: true as const, message: parsed.message })),
          );
        }),
        catchError((error) =>
          of(this.toApiError(error, 'Failed to update stock transfer.')),
        ),
      );
  }

  /** POST /IssueStock */
  createIssueStockViaApi(issue: {
    branchId: string;
    branchName?: string;
    storeLocationId: string;
    locationName?: string;
    issuedTo: string;
    recipientPhone?: string;
    recipientLocation?: string;
    issueDate: string;
    notes?: string;
    status?: StockIssue['status'];
    lines: Array<{
      inventoryItemID: number;
      quantity: number;
      unitCost?: number;
      itemName?: string;
      isReserved?: boolean;
      isRentable?: boolean;
    }>;
  }): Observable<InventoryApiResult> {
    const stockIssueLines = issue.lines.filter((line) => {
      if (line.isReserved) {
        return true;
      }
      const catalog = this.items().find(
        (row) => String(row.id) === String(line.inventoryItemID),
      );
      return !(line.isRentable ?? catalog?.isRentable === true);
    });

    if (!stockIssueLines.length) {
      return of({
        success: false,
        message:
          'Rentable items cannot be posted as issued stock. They must be checked out so they return via Check-In.',
      });
    }

    const issueForPost = { ...issue, lines: stockIssueLines };
    const issuedLines = stockIssueLines.filter((line) => !line.isReserved);
    const totalQty = issuedLines.reduce((sum, line) => sum + line.quantity, 0);
    const totalValue = issuedLines.reduce(
      (sum, line) => sum + line.quantity * (line.unitCost ?? 0),
      0,
    );
    const itemName = issuedLines
      .map((line) => line.itemName)
      .filter((name): name is string => !!name?.trim())
      .join(', ');
    const branchLabel = issue.branchName?.trim() || issue.branchId;
    const issuedStatus = issue.status ?? 'issued';

    if (!environment.apiUrl) {
      this.applyLocalIssueStockEffects(issueForPost);
      if (issuedLines.length && issuedStatus === 'issued') {
        this.addStockIssue({
          issuedTo: issue.issuedTo,
          recipientPhone: issue.recipientPhone,
          recipientLocation: issue.recipientLocation,
          branch: branchLabel,
          location: issue.locationName,
          locationId: issue.storeLocationId,
          itemName,
          issueDate: issue.issueDate,
          items: totalQty,
          totalValue,
          status: issuedStatus,
        });
      }
      const reservedLines = stockIssueLines.filter((line) => line.isReserved);
      if (reservedLines.length) {
        const reservedQty = reservedLines.reduce(
          (sum, line) => sum + line.quantity,
          0,
        );
        this.addStockIssue({
          issuedTo: issue.issuedTo,
          recipientPhone: issue.recipientPhone,
          recipientLocation: issue.recipientLocation,
          branch: branchLabel,
          location: issue.locationName,
          locationId: issue.storeLocationId,
          itemName: reservedLines
            .map((line) => line.itemName)
            .filter((name): name is string => !!name?.trim())
            .join(', '),
          issueDate: issue.issueDate,
          items: reservedQty,
          totalValue: reservedLines.reduce(
            (sum, line) => sum + line.quantity * (line.unitCost ?? 0),
            0,
          ),
          status: 'reserved',
          isReservedHold: true,
        });
      }
      return of({ success: true });
    }

    const statusMap: Record<StockIssue['status'], string> = {
      pending: 'Pending',
      reserved: 'Pending',
      issued: 'Completed',
      cancelled: 'Cancelled',
    };

    return this.api
      .post<unknown>('/IssueStock', {
        branchID: Number(issue.branchId) || 0,
        storeLocationID: Number(issue.storeLocationId) || 0,
        issuedTo: issue.issuedTo,
        recipientPhone: issue.recipientPhone ?? null,
        recipientLocation: issue.recipientLocation ?? null,
        issueDate: issue.issueDate,
        createdByUserID: this.auth.currentUserId(),
        notes: issue.notes ?? null,
        status: statusMap[issuedStatus] ?? 'Completed',
        lines: stockIssueLines.map((line) => ({
          InventoryItemID: line.inventoryItemID,
          Quantity: line.quantity,
          UnitCost: line.unitCost ?? 0,
          IsReserved: !!line.isReserved,
        })),
      })
      .pipe(
        tap((payload) => {
          const summary =
            (payload as { summary?: unknown; Summary?: unknown })?.summary ??
            (payload as { Summary?: unknown })?.Summary ??
            null;
          const sets = ((
            payload as { resultSets?: unknown[][]; ResultSets?: unknown[][] }
          ).resultSets ??
            (payload as { ResultSets?: unknown[][] }).ResultSets ??
            []) as unknown[][];
          const firstRow = Array.isArray(sets[0]) ? sets[0][0] : null;
          const source = summary ?? firstRow ?? payload;

          const issueNumber =
            pickString(
              source,
              'IssueNumber',
              'issueNumber',
              'TransactionNumber',
            ) || `IS${Date.now()}`;
          const id =
            pickString(source, 'StockTransactionID', 'stockTransactionID') ||
            crypto.randomUUID();

          const reservedId =
            pickString(
              source,
              'ReservedStockTransactionID',
              'reservedStockTransactionID',
            ) || '';
          const reservedNumber =
            pickString(source, 'ReservedIssueNumber', 'reservedIssueNumber') ||
            '';
          const reservedLines = stockIssueLines.filter(
            (line) => line.isReserved,
          );
          const reservedQty = reservedLines.reduce(
            (sum, line) => sum + line.quantity,
            0,
          );
          const reservedItemName = reservedLines
            .map((line) => line.itemName)
            .filter((name): name is string => !!name?.trim())
            .join(', ');

          if (issuedLines.length && issuedStatus === 'issued') {
            this.stockIssues.update((list) => [
              {
                id,
                issueNumber,
                issuedTo: issue.issuedTo,
                recipientPhone: issue.recipientPhone,
                recipientLocation: issue.recipientLocation,
                branch: branchLabel,
                location: issue.locationName,
                locationId: issue.storeLocationId,
                itemName,
                issueDate: issue.issueDate,
                items: totalQty,
                totalValue,
                status: issuedStatus,
              },
              ...list.filter(
                (row) => row.id !== id && row.issueNumber !== issueNumber,
              ),
            ]);
          }

          if (
            reservedLines.length &&
            (reservedId || (!issuedLines.length && id))
          ) {
            const holdId = reservedId || id;
            const holdNumber = reservedNumber || issueNumber;
            this.stockIssues.update((list) => [
              {
                id: holdId,
                issueNumber: holdNumber,
                issuedTo: issue.issuedTo,
                recipientPhone: issue.recipientPhone,
                recipientLocation: issue.recipientLocation,
                branch: branchLabel,
                location: issue.locationName,
                locationId: issue.storeLocationId,
                itemName: reservedItemName || itemName,
                issueDate: issue.issueDate,
                items: reservedQty || totalQty,
                totalValue: reservedLines.reduce(
                  (sum, line) => sum + line.quantity * (line.unitCost ?? 0),
                  0,
                ),
                status: 'reserved' as const,
                isReservedHold: true,
              },
              ...list.filter(
                (row) => row.id !== holdId && row.issueNumber !== holdNumber,
              ),
            ]);
          }

          this.refreshFromApi();
        }),
        map(() => ({ success: true })),
        catchError((error) =>
          of(this.toApiError(error, 'Failed to issue stock.')),
        ),
      );
  }

  /** Mock/local: hold reserved qty so Available Qty drops before API refresh returns. */
  applyLocalReserveStockEffects(issue: {
    branchId: string;
    storeLocationId: string;
    locationName?: string;
    lines: Array<{ inventoryItemID: number; quantity: number }>;
  }): void {
    this.applyLocalIssueStockEffects({
      ...issue,
      lines: issue.lines.map((line) => ({ ...line, isReserved: true })),
    });
  }

  /** Mock-only: decrement on-hand for a rentable checkout without creating Issued Stock. */
  applyLocalCheckoutStockEffects(issue: {
    branchId: string;
    storeLocationId: string;
    locationName?: string;
    lines: Array<{ inventoryItemID: number; quantity: number }>;
  }): void {
    this.applyLocalIssueStockEffects({
      ...issue,
      lines: issue.lines.map((line) => ({ ...line, isReserved: false })),
    });
  }

  /** Mock/local: release a prior reserved hold. */
  applyLocalReleaseReserveStockEffects(issue: {
    branchId: string;
    storeLocationId: string;
    locationName?: string;
    lines: Array<{ inventoryItemID: number; quantity: number }>;
  }): void {
    const branchId = issue.branchId;
    const locationId = issue.storeLocationId;
    const locationName = issue.locationName?.trim().toLowerCase();

    this.stockSnapshots.update((list) =>
      list.map((snap) => {
        const line = issue.lines.find(
          (row) => String(row.inventoryItemID) === String(snap.inventoryItemId),
        );
        if (!line) {
          return snap;
        }
        const branchOk =
          !snap.branchId ||
          snap.branchId === branchId ||
          Number(snap.branchId) === Number(branchId);
        const locationOk =
          (snap.locationId && snap.locationId === locationId) ||
          (!!locationName &&
            snap.location.trim().toLowerCase() === locationName);
        if (!branchOk || !locationOk) {
          return snap;
        }
        return {
          ...snap,
          reservedQuantity: Math.max(
            0,
            (snap.reservedQuantity ?? 0) - line.quantity,
          ),
        };
      }),
    );
  }

  private applyLocalIssueStockEffects(issue: {
    branchId: string;
    storeLocationId: string;
    locationName?: string;
    lines: Array<{
      inventoryItemID: number;
      quantity: number;
      isReserved?: boolean;
    }>;
  }): void {
    const branchId = issue.branchId;
    const locationId = issue.storeLocationId;
    const locationName = issue.locationName?.trim().toLowerCase();

    this.stockSnapshots.update((list) =>
      list.map((snap) => {
        const line = issue.lines.find(
          (row) => String(row.inventoryItemID) === String(snap.inventoryItemId),
        );
        if (!line) {
          return snap;
        }
        const branchOk =
          !snap.branchId ||
          snap.branchId === branchId ||
          Number(snap.branchId) === Number(branchId);
        const locationOk =
          (snap.locationId && snap.locationId === locationId) ||
          (!!locationName &&
            snap.location.trim().toLowerCase() === locationName);
        if (!branchOk || !locationOk) {
          return snap;
        }
        if (line.isReserved) {
          return {
            ...snap,
            reservedQuantity: (snap.reservedQuantity ?? 0) + line.quantity,
          };
        }
        return {
          ...snap,
          quantity: Math.max(0, snap.quantity - line.quantity),
        };
      }),
    );
  }

  fetchItems(
    query: ListQuery = {},
  ): Observable<PaginatedResult<InventoryItem>> {
    return of(this.paginate(this.filterItems(this.items(), query), query));
  }

  getItemByCode(code: string): InventoryItem | undefined {
    return this.items().find((item) => item.code === code);
  }

  addItem(item: Omit<InventoryItem, 'id'>): InventoryItem {
    const created: InventoryItem = { ...item, id: crypto.randomUUID() };
    this.items.update((list) => [...list, created]);
    this.persistItems();
    if (!environment.apiUrl) {
      this.syncStockSnapshots();
    }
    return created;
  }

  updateItem(id: string, patch: Partial<InventoryItem>): void {
    this.items.update((list) =>
      list.map((item) => (item.id === id ? { ...item, ...patch } : item)),
    );
    this.persistItems();
    if (!environment.apiUrl) {
      this.syncStockSnapshots();
    }
  }

  deleteItem(id: string): void {
    this.items.update((list) => list.filter((item) => item.id !== id));
    this.persistItems();
    if (!environment.apiUrl) {
      this.syncStockSnapshots();
    }
  }

  /** Local current-stock quantity update (no stock-adjustment API yet). */
  updateStockSnapshotQuantity(stockBalanceId: string, quantity: number): void {
    const qty = Math.max(0, quantity);
    this.stockSnapshots.update((list) =>
      list.map((row) => {
        if (row.id !== stockBalanceId) {
          return row;
        }
        return {
          ...row,
          quantity: qty,
          stockStatus: (qty <= 0
            ? 'critical'
            : qty <= 10
              ? 'low'
              : 'ok') as StockSnapshot['stockStatus'],
          lastUpdated: new Date().toISOString().slice(0, 10),
        };
      }),
    );

    // Keep catalog stockLevel in sync when the snapshot is tied to an inventory item.
    const snap = this.stockSnapshots().find((row) => row.id === stockBalanceId);
    const inventoryItemId = (
      snap as StockSnapshot & { inventoryItemId?: string }
    )?.inventoryItemId;
    if (inventoryItemId) {
      this.items.update((list) =>
        list.map((item) =>
          item.id === inventoryItemId ? { ...item, stockLevel: qty } : item,
        ),
      );
    }
  }

  /** PUT /ItemMaster/{id} — updates catalog + stock balance in the database. */
  updateItemViaApi(
    id: string,
    item: Omit<InventoryItem, 'id'> & {
      categoryId?: string;
      unitId?: string;
      branchId?: string;
      locationId?: string;
    },
  ): Observable<InventoryApiResult> {
    if (!environment.apiUrl) {
      this.updateItem(id, item);
      return of({ success: true });
    }

    const categoryId = Number(item.categoryId ?? item.category);
    const unitId = Number(item.unitId ?? item.unit);
    const branchId = resolveBranchId(this.auth, item.branchId ?? item.branch);
    const locationId = Number(item.locationId ?? item.location);
    const inventoryItemId = Number(id);

    if (!Number.isFinite(inventoryItemId) || inventoryItemId <= 0) {
      return of({ success: false, message: 'Invalid inventory item id.' });
    }

    return this.api
      .put(`/ItemMaster/${inventoryItemId}`, {
        itemName: item.name,
        itemDescription: item.name,
        itemCategoryID:
          Number.isFinite(categoryId) && categoryId > 0 ? categoryId : 0,
        unitOfMeasureID: Number.isFinite(unitId) && unitId > 0 ? unitId : 0,
        branchID: branchId,
        storeLocationID:
          Number.isFinite(locationId) && locationId > 0
            ? locationId
            : undefined,
        quantityOnHand: item.stockLevel,
        reorderLevel: item.reorderLevel,
        itemType: item.isRentable ? 'RENTAL_EQUIPMENT' : 'CONSUMABLE',
        trackingMethod: 'Quantity',
        standardCost: item.price,
        sellingPrice: item.price,
        isConsumable: !item.isRentable,
        isRentable: !!item.isRentable,
        isReturnable: !!item.isRentable,
        isActive: item.status === 'active',
      })
      .pipe(
        switchMap(() => this.refreshInventoryAfterStockMove()),
        map(() => ({ success: true as const })),
        catchError((error) =>
          of(this.toApiError(error, 'Failed to update inventory item.')),
        ),
      );
  }

  /**
   * Remove an item from the active catalog.
   * Tries DELETE /ItemMaster/{id}; if the gateway has no delete verb, deactivates via PUT.
   */
  deleteItemViaApi(item: InventoryItem): Observable<InventoryApiResult> {
    if (!environment.apiUrl) {
      this.deleteItem(item.id);
      return of({ success: true });
    }

    const inventoryItemId = Number(item.id);
    if (!Number.isFinite(inventoryItemId) || inventoryItemId <= 0) {
      return of({ success: false, message: 'Invalid inventory item id.' });
    }

    if (item.status === 'inactive') {
      return of({ success: true });
    }

    const { id: _id, ...rest } = item;
    return this.api.delete(`/ItemMaster/${inventoryItemId}`).pipe(
      tap(() => this.refreshFromApi()),
      map(() => ({ success: true })),
      catchError((error) => {
        if (!this.isDeleteUnsupported(error)) {
          return of(this.toApiError(error, 'Failed to delete inventory item.'));
        }
        return this.updateItemViaApi(item.id, {
          ...rest,
          status: 'inactive',
        });
      }),
    );
  }

  private isDeleteUnsupported(error: unknown): boolean {
    if (!(error instanceof HttpErrorResponse)) {
      return false;
    }
    if (error.status === 404 || error.status === 405 || error.status === 501) {
      return true;
    }
    const message = String(
      (typeof error.error === 'object' && error.error
        ? ((error.error as { message?: string; Message?: string }).message ??
          (error.error as { Message?: string }).Message)
        : error.error) ??
        error.message ??
        '',
    ).toLowerCase();
    return (
      message.includes('not implemented') ||
      message.includes('not supported') ||
      message.includes('not available') ||
      message.includes('method not allowed')
    );
  }

  /** POST /CurrentStock/adjust — persists quantity and refreshes Register Items + Current Stock. */
  adjustStockViaApi(input: {
    stockBalanceId: string;
    inventoryItemId?: string;
    branchId?: string;
    locationId?: string;
    newQuantity: number;
    reason?: string;
    notes?: string;
  }): Observable<InventoryApiResult> {
    if (!environment.apiUrl) {
      this.updateStockSnapshotQuantity(input.stockBalanceId, input.newQuantity);
      return of({ success: true });
    }

    return this.api
      .post('/CurrentStock/adjust', {
        stockBalanceID: Number(input.stockBalanceId) || null,
        inventoryItemID: Number(input.inventoryItemId) || null,
        branchID: resolveBranchId(this.auth, input.branchId),
        storeLocationID: Number(input.locationId) || null,
        newQuantity: input.newQuantity,
        reason: input.reason ?? null,
        notes: input.notes ?? null,
        createdByUserID: this.auth.currentUserId(),
      })
      .pipe(
        switchMap((response) => {
          if (response && typeof response === 'object') {
            const body = response as Record<string, unknown>;
            if (body['success'] === false) {
              return of({
                success: false as const,
                message: String(
                  body['message'] ?? body['error'] ?? 'Failed to adjust stock.',
                ),
              });
            }
          }
          return this.refreshInventoryAfterStockMove().pipe(
            map(() => ({ success: true as const })),
          );
        }),
        catchError((error) =>
          of(this.toApiError(error, 'Failed to adjust stock.')),
        ),
      );
  }

  updateAsset(id: string, patch: Partial<AssetRegisterEntry>): void {
    this.assets.update((list) =>
      list.map((asset) => (asset.id === id ? { ...asset, ...patch } : asset)),
    );
  }

  deleteAsset(id: string): void {
    this.assets.update((list) => list.filter((asset) => asset.id !== id));
  }

  updateGoodsReceived(id: string, patch: Partial<GoodsReceivedNote>): void {
    this.goodsReceived.update((list) =>
      list.map((note) => (note.id === id ? { ...note, ...patch } : note)),
    );
  }

  deleteGoodsReceived(id: string): void {
    this.goodsReceived.update((list) => list.filter((note) => note.id !== id));
  }

  /** DELETE /GoodsReceived/{id} — cancels receipt and reverses stock if posted. */
  deleteGoodsReceivedViaApi(id: string): Observable<InventoryApiResult> {
    if (!environment.apiUrl) {
      this.deleteGoodsReceived(id);
      return of({ success: true });
    }

    const stockTransactionId = Number(id);
    if (!Number.isFinite(stockTransactionId) || stockTransactionId <= 0) {
      return of({
        success: false,
        message: 'Invalid receipt id. Refresh Goods Received and try again.',
      });
    }

    return this.api
      .delete<unknown>(`/GoodsReceived/${stockTransactionId}`)
      .pipe(
        tap(() => {
          this.updateGoodsReceived(id, { status: 'cancelled' });
          this.refreshFromApi();
        }),
        map(() => ({ success: true })),
        catchError((error) =>
          of(this.toApiError(error, 'Failed to cancel goods receipt.')),
        ),
      );
  }

  updateStockIssue(id: string, patch: Partial<StockIssue>): void {
    this.stockIssues.update((list) =>
      list.map((issue) => (issue.id === id ? { ...issue, ...patch } : issue)),
    );
  }

  /** PUT /IssueStock/{id} — updates status / issued-to in the database. */
  updateIssueStockViaApi(
    id: string,
    patch: {
      status: StockIssue['status'];
      issuedTo?: string;
      issueDate?: string;
      notes?: string;
    },
  ): Observable<InventoryApiResult> {
    if (!environment.apiUrl) {
      this.updateStockIssue(id, patch);
      return of({ success: true });
    }

    const stockTransactionId = Number(id);
    if (!Number.isFinite(stockTransactionId) || stockTransactionId <= 0) {
      return of({
        success: false,
        message: 'Invalid issue id. Refresh Issued Stock and try again.',
      });
    }

    const statusMap: Record<StockIssue['status'], string> = {
      pending: 'Pending',
      reserved: 'Pending',
      issued: 'Completed',
      cancelled: 'Cancelled',
    };

    return this.api
      .put<unknown>(`/IssueStock/${stockTransactionId}`, {
        status: statusMap[patch.status] ?? 'Completed',
        issuedTo: patch.issuedTo ?? null,
        issueDate: patch.issueDate ?? null,
        notes: patch.notes ?? null,
      })
      .pipe(
        tap(() => {
          const nextStatus =
            patch.status === 'reserved'
              ? 'reserved'
              : patch.status === 'issued'
                ? 'issued'
                : patch.status;
          const patchLocal: Partial<StockIssue> = {
            status: nextStatus,
            issuedTo: patch.issuedTo,
            issueDate: patch.issueDate,
          };
          if (patch.status === 'issued' || patch.status === 'cancelled') {
            patchLocal.isReservedHold = false;
            if (patch.status === 'issued') {
              patchLocal.status = 'issued';
            }
          }
          this.updateStockIssue(id, patchLocal);
          this.refreshFromApi();
        }),
        map(() => ({ success: true })),
        catchError((error) =>
          of(this.toApiError(error, 'Failed to update issue stock.')),
        ),
      );
  }

  deleteStockIssue(id: string): void {
    this.stockIssues.update((list) => list.filter((issue) => issue.id !== id));
  }

  updateStockAdjustment(id: string, patch: Partial<StockAdjustment>): void {
    this.stockAdjustments.update((list) =>
      list.map((row) => (row.id === id ? { ...row, ...patch } : row)),
    );
  }

  deleteStockAdjustment(id: string): void {
    this.stockAdjustments.update((list) => list.filter((row) => row.id !== id));
  }

  updateStockTransfer(id: string, patch: Partial<StockTransfer>): void {
    this.stockTransfers.update((list) =>
      list.map((row) => (row.id === id ? { ...row, ...patch } : row)),
    );
  }

  deleteStockTransfer(id: string): void {
    this.stockTransfers.update((list) => list.filter((row) => row.id !== id));
  }

  getStockSnapshots(): StockSnapshot[] {
    return this.stockSnapshots();
  }

  getAssets(): AssetRegisterEntry[] {
    return this.assets();
  }

  getGoodsReceived(): GoodsReceivedNote[] {
    return this.goodsReceived();
  }

  getStockIssues(): StockIssue[] {
    return this.stockIssues();
  }

  getStockAdjustments(): StockAdjustment[] {
    return this.stockAdjustments();
  }

  getStockTransfers(): StockTransfer[] {
    return this.stockTransfers();
  }

  addAsset(asset: Omit<AssetRegisterEntry, 'id'>): AssetRegisterEntry {
    const created: AssetRegisterEntry = { ...asset, id: crypto.randomUUID() };
    this.assets.update((list) => [...list, created]);
    return created;
  }

  addGoodsReceived(
    note: Omit<GoodsReceivedNote, 'id' | 'grnNumber'>,
  ): GoodsReceivedNote {
    const next = this.goodsReceived().length + 1;
    const created: GoodsReceivedNote = {
      ...note,
      id: crypto.randomUUID(),
      grnNumber: `GRN-2026-${String(142 + next).padStart(4, '0')}`,
    };
    this.goodsReceived.update((list) => [created, ...list]);
    return created;
  }

  addStockIssue(issue: Omit<StockIssue, 'id' | 'issueNumber'>): StockIssue {
    const next = this.stockIssues().length + 1;
    const created: StockIssue = {
      ...issue,
      id: crypto.randomUUID(),
      issueNumber: `ISS-2026-${String(89 + next).padStart(4, '0')}`,
    };
    this.stockIssues.update((list) => [created, ...list]);
    return created;
  }

  addStockAdjustment(
    adjustment: Omit<StockAdjustment, 'id' | 'adjustmentNumber'>,
  ): StockAdjustment {
    const next = this.stockAdjustments().length + 1;
    const created: StockAdjustment = {
      ...adjustment,
      id: crypto.randomUUID(),
      adjustmentNumber: `ADJ-2026-${String(34 + next).padStart(4, '0')}`,
    };
    this.stockAdjustments.update((list) => [created, ...list]);
    return created;
  }

  addStockTransfer(
    transfer: Omit<StockTransfer, 'id' | 'transferNumber'>,
  ): StockTransfer {
    const next = this.stockTransfers().length + 1;
    const created: StockTransfer = {
      ...transfer,
      id: crypto.randomUUID(),
      transferNumber: `TRF-2026-${String(12 + next).padStart(4, '0')}`,
    };
    this.stockTransfers.update((list) => [created, ...list]);
    return created;
  }

  syncStockSnapshots(): void {
    if (!environment.apiUrl) {
      return;
    }

    this.stockSnapshots.set(
      this.items().map((item) => ({
        id: item.id,
        itemCode: item.code,
        itemName: item.name,
        category: item.category,
        branch: item.branch,
        location: item.location || 'Warehouse A',
        quantity: item.stockLevel,
        unit: item.unit,
        unitValue: item.price,
        stockStatus:
          item.stockLevel <= item.reorderLevel / 2
            ? 'critical'
            : item.stockLevel <= item.reorderLevel
              ? 'low'
              : 'ok',
      })),
    );
  }

  private applyItemMaster(payload: GenericMultiResultDto): void {
    // Prefer the item-list grid (ItemCode + InventoryItemID), not category/branch lookups.
    const codedSet = findResultSetWithColumn(payload, 'ItemCode');
    const itemRows = codedSet.filter((row) => {
      const id = pickString(row, 'InventoryItemID', 'ItemID', 'id');
      const code = pickString(row, 'ItemCode', 'itemCode', 'code');
      return !!id && !!code;
    });
    const rows = itemRows.length ? itemRows : resultSetAt(payload, 4);
    if (!rows.length) {
      return;
    }

    const stockByItemId = new Map(
      this.stockSnapshots().map((snap) => {
        const inventoryItemId =
          (snap as StockSnapshot & { inventoryItemId?: string })
            .inventoryItemId ?? '';
        return [inventoryItemId, snap] as const;
      }),
    );

    this.items.set(
      filterRowsByUserBranch(
        this.auth,
        rows
          .map((row, index) => {
            const statusRaw = pickString(
              row,
              'Status',
              'ItemStatus',
              'status',
              'IsActive',
            ).toLowerCase();
            const stockLevel = pickNumber(
              row,
              'StockLevel',
              'QuantityOnHand',
              'QuantityAvailable',
              'quantity',
            );
            const reorder = pickNumber(
              row,
              'ReorderLevel',
              'reorderLevel',
              'MinimumStockLevel',
            );
            const id =
              pickString(row, 'InventoryItemID', 'ItemID', 'id') ||
              String(index + 1);
            const fromStock = stockByItemId.get(id);
            const branchId =
              pickString(row, 'BranchID', 'branchID') ||
              fromStock?.branchId ||
              '';

            return {
              id,
              code: pickString(row, 'ItemCode', 'itemCode', 'code'),
              name: pickString(row, 'ItemName', 'itemName', 'name'),
              category: pickString(
                row,
                'CategoryName',
                'category',
                'ItemCategory',
              ).trim(),
              categoryId: pickString(
                row,
                'ItemCategoryID',
                'itemCategoryID',
              ).trim(),
              unit: (
                pickString(row, 'UnitName', 'UnitCode', 'unit') || 'Unit'
              ).trim(),
              unitId: pickString(
                row,
                'UnitOfMeasureID',
                'unitOfMeasureID',
              ).trim(),
              stockLevel,
              reorderLevel: reorder || Math.max(1, Math.floor(stockLevel / 2)),
              price: pickNumber(
                row,
                'SellingPrice',
                'StandardCost',
                'price',
                'UnitCost',
              ),
              status: statusRaw.includes('discontinu')
                ? ('discontinued' as const)
                : statusRaw.includes('inactive') ||
                    statusRaw === '0' ||
                    statusRaw === 'false'
                  ? ('inactive' as const)
                  : ('active' as const),
              branch:
                pickString(row, 'BranchName', 'branch') ||
                fromStock?.branch ||
                '',
              branchId,
              location:
                pickString(row, 'LocationName', 'location') ||
                fromStock?.location ||
                '',
              locationId: pickString(row, 'StoreLocationID', 'storeLocationID'),
              isRentable:
                pickString(row, 'IsRentable', 'isRentable').toLowerCase() ===
                  'true' ||
                pickNumber(row, 'IsRentable') === 1 ||
                pickString(row, 'ItemType', 'itemType')
                  .toUpperCase()
                  .includes('RENTAL'),
              icon: 'bi-box-seam',
            };
          })
          .filter((item) => {
            // Register Items is branch-scoped: drop rows with no branch after mapping.
            const userBranchId = this.auth.currentBranchId();
            if (userBranchId <= 0) {
              return true;
            }
            return !!item.branchId && item.branchId === String(userBranchId);
          }),
        (item) => item.branchId,
      ),
    );
  }

  private applyCurrentStock(payload: GenericMultiResultDto): void {
    const matched = findResultSetWithColumn(
      payload,
      'StockBalanceID',
      'QuantityOnHand',
      'QuantityAvailable',
    );
    const rows = matched.length ? matched : resultSetAt(payload, 0);
    const snapshots: Array<StockSnapshot & { inventoryItemId?: string }> = rows
      .map((row, index) => {
        // Prefer on-hand for Current Stock display; available can be lower due to reserves.
        const qty = pickNumber(
          row,
          'QuantityOnHand',
          'QuantityAvailable',
          'quantityAvailable',
          'quantity',
        );
        const reorder = pickNumber(row, 'ReorderLevel', 'reorderLevel');
        const reserved = pickNumber(
          row,
          'QuantityReserved',
          'ReservedQuantity',
          'reserved',
        );
        const statusRaw = pickString(
          row,
          'StockStatus',
          'stockStatus',
        ).toLowerCase();
        const stockStatus: StockSnapshot['stockStatus'] = statusRaw.includes(
          'out',
        )
          ? 'critical'
          : statusRaw.includes('low')
            ? 'low'
            : qty <= 0
              ? 'critical'
              : qty <= reorder && reorder > 0
                ? 'low'
                : 'ok';
        const inventoryItemId = pickString(
          row,
          'InventoryItemID',
          'inventoryItemID',
        );
        const stockBalanceId = pickString(
          row,
          'StockBalanceID',
          'stockBalanceID',
        );
        const locationId = pickString(
          row,
          'StoreLocationID',
          'storeLocationID',
          'LocationID',
        );
        const location = pickString(row, 'LocationName', 'location').trim();
        const itemName = pickString(row, 'ItemName', 'itemName', 'name').trim();

        return {
          // Always unique per balance/location — never fall back to InventoryItemID alone
          // (same item at two locations would collapse to one card in the UI).
          id:
            stockBalanceId ||
            (inventoryItemId && locationId
              ? `bal-${inventoryItemId}-loc-${locationId}`
              : `bal-${index + 1}`),
          inventoryItemId,
          itemCode: pickString(row, 'ItemCode', 'itemCode', 'code'),
          itemName,
          category: pickString(row, 'CategoryName', 'category'),
          branch: pickString(row, 'BranchName', 'branch').trim(),
          branchId: pickString(row, 'BranchID', 'branchID', 'branchId'),
          location,
          locationId,
          quantity: qty,
          reservedQuantity: reserved,
          unit: pickString(row, 'UnitCode', 'unit') || 'Unit',
          unitValue: pickNumber(row, 'StandardCost', 'unitValue', 'UnitCost'),
          stockStatus,
          lastUpdated: pickDate(
            row,
            'LastUpdatedDate',
            'LastUpdated',
            'UpdatedAt',
            'lastUpdated',
          ),
        };
      })
      .filter((snap) => !!snap.itemName || !!snap.inventoryItemId);

    this.stockSnapshots.set(
      filterRowsByUserBranch(this.auth, snapshots, (snap) => snap.branchId),
    );

    // Do not patch Register Items stockLevel from a single location row here.
    // loadItemMasterAndStock() calls syncItemMasterStockFromSnapshots() for branch totals.
  }

  private applyGoodsReceived(payload: GenericMultiResultDto): void {
    // Match receipt rows only — SupplierName also exists on the suppliers lookup set.
    const matched = findResultSetWithColumn(payload, 'ReceiptNumber');
    const rows = matched.length ? matched : resultSetAt(payload, 3);
    const receiptRows = rows.filter(
      (row) =>
        !!pickString(
          row,
          'ReceiptNumber',
          'TransactionNumber',
          'StockTransactionID',
        ),
    );

    this.goodsReceived.set(
      filterRowsByUserBranch(
        this.auth,
        receiptRows.map((row, index) => {
          const statusRaw = pickString(
            row,
            'Status',
            'ReceiptStatus',
            'status',
          ).toLowerCase();
          const lineCount = pickNumber(
            row,
            'TotalItems',
            'ItemCount',
            'LineCount',
          );
          const quantity = pickNumber(
            row,
            'TotalQuantity',
            'Items',
            'items',
            'Quantity',
          );
          return {
            id:
              pickString(row, 'StockTransactionID', 'GoodsReceiptID', 'id') ||
              String(index + 1),
            grnNumber:
              pickString(
                row,
                'ReceiptNumber',
                'TransactionNumber',
                'GRNNumber',
                'ReferenceNumber',
                'grnNumber',
              ) || `GRN-${index + 1}`,
            supplier: pickString(row, 'SupplierName', 'supplier'),
            branch: pickString(row, 'BranchName', 'branch'),
            branchId: pickString(row, 'BranchID', 'branchID'),
            location: pickString(
              row,
              'LocationName',
              'StoreLocation',
              'location',
            ),
            itemName: pickString(row, 'ItemName', 'ItemsSummary', 'itemName'),
            receivedDate: pickDate(
              row,
              'ReceiptDate',
              'ReceivedDate',
              'TransactionDate',
              'receivedDate',
            ),
            // Prefer total quantity so "12 items" matches stock received, not line count.
            items: quantity || lineCount,
            totalValue: pickNumber(
              row,
              'TotalValue',
              'totalValue',
              'TotalCost',
            ),
            status:
              statusRaw.includes('cancel') || statusRaw.includes('return')
                ? ('cancelled' as const)
                : statusRaw.includes('draft') || statusRaw.includes('pending')
                  ? ('draft' as const)
                  : ('posted' as const),
            isRentable:
              pickString(
                row,
                'ItemsAreRentable',
                'isRentable',
              ).toLowerCase() === 'true' ||
              pickNumber(row, 'ItemsAreRentable') === 1,
          };
        }),
        (note) => (note as GoodsReceivedNote & { branchId?: string }).branchId,
      ) as GoodsReceivedNote[],
    );
  }

  private applyIssueStock(payload: GenericMultiResultDto): void {
    // Prefer the Recent Issues grid (IssueNumber). Do not fall back to available-items
    // (that set has ItemName/Status and used to pollute this list when indexing was wrong).
    const matched = findResultSetWithColumn(payload, 'IssueNumber');
    const fallback = resultSetAt(payload, 2);
    const candidate = matched.length ? matched : fallback;
    const rows = candidate.filter(
      (row) =>
        !!pickString(row, 'IssueNumber', 'TransactionNumber', 'issueNumber'),
    );

    if (!rows.length) {
      // Do not wipe locally known issues when GET returns an empty/mismatched grid.
      return;
    }

    this.stockIssues.set(
      filterRowsByUserBranch(
        this.auth,
        rows.map((row, index) => {
          const statusRaw = pickString(
            row,
            'IssueStatus',
            'Status',
            'status',
          ).toLowerCase();
          const notesRaw = pickString(row, 'Notes', 'notes');
          const issueNumber =
            pickString(
              row,
              'IssueNumber',
              'TransactionNumber',
              'issueNumber',
            ) || `ISS-${index + 1}`;
          const isReservedHold =
            notesRaw.includes('[[STOCK_RESERVE]]') ||
            issueNumber.toUpperCase().startsWith('RSV') ||
            statusRaw.includes('reserv');
          const status: StockIssue['status'] = statusRaw.includes('cancel')
            ? 'cancelled'
            : isReservedHold &&
                (statusRaw.includes('pending') || statusRaw.includes('reserv'))
              ? 'reserved'
              : statusRaw.includes('pending')
                ? 'pending'
                : 'issued';
          return {
            id:
              pickString(
                row,
                'StockTransactionID',
                'StockIssueID',
                'IssueNumber',
                'id',
              ) || String(index + 1),
            issueNumber,
            issuedTo: pickString(
              row,
              'IssuedTo',
              'CaseRef',
              'FuneralCaseRef',
              'Customer',
              'issuedTo',
              'ReferenceNumber',
            ),
            recipientPhone: pickString(row, 'RecipientPhone', 'recipientPhone'),
            recipientLocation: pickString(
              row,
              'RecipientLocation',
              'recipientLocation',
            ),
            branch: pickString(row, 'BranchName', 'branch'),
            branchId: pickString(row, 'BranchID', 'branchID'),
            location: pickString(
              row,
              'LocationName',
              'StoreLocationName',
              'location',
            ),
            locationId: pickString(
              row,
              'StoreLocationID',
              'SourceLocationID',
              'locationId',
            ),
            itemName: pickString(row, 'ItemName', 'ItemsSummary', 'itemName'),
            inventoryItemId:
              pickString(
                row,
                'FirstInventoryItemID',
                'InventoryItemID',
                'inventoryItemId',
              ) || undefined,
            issueDate: pickDate(
              row,
              'IssueDate',
              'IssuedDate',
              'TransactionDate',
              'issueDate',
            ),
            items: (() => {
              const qty = pickNumber(row, 'TotalQuantity', 'Quantity');
              return qty > 0
                ? qty
                : pickNumber(row, 'TotalItems', 'ItemCount', 'Items', 'items');
            })(),
            totalValue: pickNumber(
              row,
              'TotalValue',
              'totalValue',
              'TotalCost',
            ),
            status,
            isReservedHold: isReservedHold && status === 'reserved',
          };
        }),
        (issue) => (issue as StockIssue & { branchId?: string }).branchId,
      ),
    );
  }

  private applyAssetsFromCheckout(payload: GenericMultiResultDto): void {
    const matched = findResultSetWithColumn(
      payload,
      'EquipmentAssetID',
      'CanCheckout',
      'DisplayStatus',
    );
    const rows = matched.length ? matched : resultSetAt(payload, 0);
    if (!rows.length) {
      return;
    }

    this.assets.set(
      rows
        .filter((row: unknown) => {
          const itemType = pickString(row, 'ItemType').toUpperCase();
          return itemType !== 'CONSUMABLE' && itemType !== 'SERVICE_ITEM';
        })
        .map((row: unknown, index: number) => {
          const statusRaw = pickString(
            row,
            'AssetStatus',
            'DisplayStatus',
            'status',
          ).toLowerCase();
          return {
            id:
              pickString(row, 'EquipmentAssetID', 'AssetNumber', 'id') ||
              String(index + 1),
            assetTag:
              pickString(row, 'AssetNumber', 'ItemCode', 'assetTag') ||
              `AST-${index + 1}`,
            name:
              pickString(row, 'ItemName', 'Asset', 'name') ||
              `Item ${index + 1}`,
            category:
              pickString(row, 'CategoryName', 'category') || 'Equipment',
            location: pickString(row, 'LocationName', 'BranchName', 'location'),
            purchaseDate: pickDate(row, 'PurchaseDate', 'purchaseDate'),
            purchaseValue: pickNumber(
              row,
              'PurchaseCost',
              'StandardCost',
              'purchaseValue',
            ),
            condition:
              pickString(row, 'CurrentCondition', 'condition') || 'Good',
            status:
              statusRaw.includes('lost') || statusRaw.includes('disposed')
                ? ('disposed' as const)
                : statusRaw.includes('retired')
                  ? ('retired' as const)
                  : ('active' as const),
          };
        }),
    );
  }

  private applyAssetsFromDamaged(payload: GenericMultiResultDto): void {
    const matched = findResultSetWithColumn(
      payload,
      'EquipmentAssetID',
      'AssetNumber',
    );
    const rows = matched.length ? matched : resultSetAt(payload, 0);
    if (!rows.length) {
      return;
    }

    this.assets.set(
      rows.map((row: unknown, index: number) => {
        const statusRaw = pickString(
          row,
          'AssetStatus',
          'status',
        ).toLowerCase();
        return {
          id:
            pickString(row, 'EquipmentAssetID', 'AssetNumber', 'id') ||
            String(index + 1),
          assetTag:
            pickString(row, 'AssetNumber', 'assetTag') || `AST-${index + 1}`,
          name: pickString(row, 'ItemName', 'Asset', 'name', 'AssetNumber'),
          category: pickString(row, 'CategoryName', 'category'),
          location: pickString(row, 'LocationName', 'BranchName', 'location'),
          purchaseDate: pickDate(row, 'PurchaseDate', 'purchaseDate'),
          purchaseValue: pickNumber(row, 'PurchaseCost', 'purchaseValue'),
          condition: pickString(row, 'CurrentCondition', 'condition') || 'Fair',
          status:
            statusRaw.includes('lost') || statusRaw.includes('disposed')
              ? ('disposed' as const)
              : statusRaw.includes('retired')
                ? ('retired' as const)
                : ('active' as const),
        };
      }),
    );
  }

  private applyStockTransfers(payload: StockTransfersResponseDto): void {
    const root = payload as StockTransfersResponseDto & {
      Transfers?: StockTransfersResponseDto['transfers'];
      Summary?: StockTransfersResponseDto['summary'];
    };
    const rows = payload.transfers ?? root.Transfers ?? [];
    if (!Array.isArray(rows)) {
      return;
    }

    this.stockTransfers.set(
      rows.map((row) => {
        const source = row as Record<string, unknown>;
        const statusRaw = pickString(source, 'status', 'Status').toLowerCase();
        const status: StockTransfer['status'] = statusRaw.includes('cancel')
          ? 'cancelled'
          : statusRaw.includes('transit')
            ? 'in-transit'
            : statusRaw.includes('pending')
              ? 'pending'
              : statusRaw.includes('complete')
                ? 'completed'
                : 'pending';

        const fromBranch = pickString(source, 'fromBranch', 'FromBranch');
        const toBranch = pickString(source, 'toBranch', 'ToBranch');
        const fromStorage = pickString(source, 'fromStorage', 'FromStorage');
        const toStorage = pickString(source, 'toStorage', 'ToStorage');
        const itemName = pickString(source, 'itemName', 'ItemName');
        const fromBranchId = pickString(
          source,
          'fromBranchID',
          'FromBranchID',
          'fromBranchId',
        );
        const toBranchId = pickString(
          source,
          'toBranchID',
          'ToBranchID',
          'toBranchId',
        );
        const stockTransactionId = pickNumber(
          source,
          'stockTransactionID',
          'StockTransactionID',
        );

        return {
          id: stockTransactionId > 0 ? String(stockTransactionId) : '',
          transferNumber:
            pickString(source, 'transferID', 'TransferID') ||
            (stockTransactionId > 0
              ? `TRF-${stockTransactionId}`
              : 'TRF-unknown'),
          fromLocation: [fromBranch, fromStorage].filter(Boolean).join(' / '),
          toLocation: [toBranch, toStorage].filter(Boolean).join(' / '),
          fromBranch: fromBranch || undefined,
          toBranch: toBranch || undefined,
          fromBranchId: fromBranchId || undefined,
          toBranchId: toBranchId || undefined,
          fromLocationName: fromStorage || undefined,
          toLocationName: toStorage || undefined,
          itemName: itemName || undefined,
          transferDate: pickDate(source, 'transferDate', 'TransferDate'),
          items: pickNumber(source, 'items', 'Items'),
          status,
        };
      }),
    );
  }

  /** Reads create/update proc payload; only fails when Success is explicitly false. */
  private parseStockTransferProcResult(
    payload: unknown,
    fallback: string,
  ): InventoryApiResult & { id?: string; transferNumber?: string } {
    const root = asDict(payload);
    if (root['success'] === false) {
      return {
        success: false,
        message: String(root['message'] ?? root['error'] ?? fallback),
      };
    }

    const data = asDict(root['data'] ?? root);
    const summary = data['summary'] ?? data['Summary'] ?? null;
    const sets = (data['resultSets'] ?? data['ResultSets'] ?? []) as unknown[];
    const firstSet = Array.isArray(sets[0]) ? sets[0] : sets;
    const firstRow = Array.isArray(firstSet) ? firstSet[0] : null;
    const source = summary ?? firstRow ?? data;

    if (this.isExplicitProcFailure(source)) {
      return {
        success: false,
        message:
          pickString(source, 'Message', 'message', 'ErrorMessage', 'error') ||
          fallback,
      };
    }

    return {
      success: true,
      id: (() => {
        const n = pickNumber(
          source,
          'StockTransactionID',
          'stockTransactionID',
        );
        return n > 0 ? String(n) : undefined;
      })(),
      transferNumber:
        pickString(
          source,
          'TransferNumber',
          'transferNumber',
          'TransactionNumber',
        ) || undefined,
    };
  }

  private isExplicitProcFailure(source: unknown): boolean {
    const procSuccess = pick(source, 'Success', 'success');
    if (
      procSuccess === undefined ||
      procSuccess === null ||
      procSuccess === ''
    ) {
      return false;
    }
    return (
      procSuccess === false ||
      procSuccess === 0 ||
      procSuccess === '0' ||
      String(procSuccess).toLowerCase() === 'false'
    );
  }

  private filterItems(
    items: InventoryItem[],
    query: ListQuery,
  ): InventoryItem[] {
    const search = query.search?.trim().toLowerCase();
    if (!search) {
      return items;
    }

    return items.filter(
      (item) =>
        item.code.toLowerCase().includes(search) ||
        item.name.toLowerCase().includes(search) ||
        item.category.toLowerCase().includes(search),
    );
  }

  private paginate<T>(items: T[], query: ListQuery): PaginatedResult<T> {
    const page = query.page ?? 1;
    const pageSize = query.pageSize ?? (items.length || 1);
    const start = (page - 1) * pageSize;

    return {
      items: items.slice(start, start + pageSize),
      total: items.length,
      page,
      pageSize,
    };
  }

  private loadItems(): InventoryItem[] {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return [...MOCK_INVENTORY_ITEMS];
    }

    try {
      return JSON.parse(raw) as InventoryItem[];
    } catch {
      localStorage.removeItem(STORAGE_KEY);
      return [];
    }
  }

  private persistItems(): void {
    if (!environment.apiUrl) {
      return;
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(this.items()));
  }
}
